import { Component, OnInit, Input } from '@angular/core';
import { FormArray, FormBuilder, Validators, FormGroup, FormControl, ValidatorFn, AbstractControl } from '@angular/forms';
import { GoalDTO } from 'src/app/planner/dto/GoalDTO';
import { GoalType } from 'src/app/planner/enums/GoalType.enum';
import { DigitalVideoMeasurementViewability } from '../../planner-edit/constants/DigitalVideoViewabilityMeasurement.const';
import * as _ from 'lodash';
import { AdvertiserInfoVO } from '../../../interfaces/AdvertiserInfo';
import { BudgetingService } from '../../../services/budgeting.service';
import { PlannerConstants } from 'src/app/planner/constants/planner.const';
import { DigitalTacticVO } from 'src/app/planner/interfaces/vo/DigitalTacticVO';
import { moveItemInArray, CdkDragDrop } from "@angular/cdk/drag-drop";
import { PlanStatus } from '../../planner-edit/constants/PlanStatus.const';
import { EnvironmentConst } from '../../planner-edit/constants/Environment.const';
import { IOptionProps } from '@amobee/component-library';
import { PlannerService } from 'src/app/planner/services/planner.service';

@Component({
  selector: 'goals',
  templateUrl: './goals.component.html',
  styleUrls: ['./goals.component.scss']
})
export class GoalsComponent implements OnInit {
  @Input() group: string;
  @Input() formArray: FormArray;
  @Input() isNew: boolean;
  @Input() hasDefaults: boolean;
  @Input() advertiserInfo: AdvertiserInfoVO;
  @Input() tacticsVO: Array<DigitalTacticVO>;
  @Input() status: string;
  showViewabilityMsg: boolean = true;
  isMobileAppEnvSelected: boolean = false;
  readonly budgetLimit = PlannerConstants.BUDGET_LIMIT;

  VCPM = {
    label: 'vCPM',
    goalTypeId: GoalType.VIEWABILITY_VCPM
  }

  CPCV = {
    label: 'CPCV',
    goalTypeId: GoalType.ENGAGEMENT_CPCV
  }

  GoalGroupConfigs = ({
    'Viewability': {
      label: 'Viewability',
      id: GoalType.DELIVERY_VIEWABILITY,
    },
    'Completion': {
      label: 'Completion',
      id: GoalType.DELIVERY_ENGAGEMENT,
    }
  })

  planStatus: any = PlanStatus;
  viewabilityMeasurement = DigitalVideoMeasurementViewability;
  goalTypeEnum = GoalType;
  selectedGoalValueType: string[] = ['Rate'];
  selectedGoalType: string[] = [this.GoalGroupConfigs.Completion.label];
  currencyAbbrevWithSymbol: string = '';
  currencySymbol: string = '';
  compatible: boolean = true;

  constructor(
    private fb: FormBuilder,
    private budgetingService: BudgetingService,
    private plannerService: PlannerService
  ) { }

  ngOnInit() {

    if (!this.isNew || this.hasDefaults) {
      //Add one more GoalDTO and patch GoalVOs again to guarantee that can get two goals data successfully
      if (!this.isNew && this.tacticsVO[this.group].goals.length === 2) {
        let isDisabled = this.isDisabled;
        this.addGoal(true)
        this.goals.patchValue(this.tacticsVO[this.group].goals);
        // If plan is generated, we have to disable goals manually
        if (isDisabled) {
          this.goals.disable();
        }
      }
      //If plan is not in draft status, we have to disable goals manually
      if (!this.isNew && this.plannerService.isPlanFormEditable(this.status)) {
        this.goals.disable();
      }
      this.updateSelectedGoals();
    }

    if (this.advertiserInfo.currency) {
      this.currencyAbbrevWithSymbol = this.budgetingService.getCurrencyAbbrevWithSymbol(this.advertiserInfo.currency);
      this.currencySymbol = this.budgetingService.getCurrencySymbol(this.advertiserInfo.currency);
    }
    this.setOptimizedGoalId(0);
    if (this.goals.value.length === 2) {
      this.setOptimizedGoalId(1);
    }
    this.checkEnvironmentChange();
  }

  updateSelectedGoals() {
    for (var i = 0; i < this.goals.value.length; i++) {
      if (this.goalId(i).value === this.goalTypeEnum.DELIVERY_ENGAGEMENT) {
        this.selectedGoalValueType[i] = 'Rate';
        this.selectedGoalType[i] = this.GoalGroupConfigs.Completion.label;
      } else if (this.goalId(i).value === this.goalTypeEnum.DELIVERY_VIEWABILITY) {
        this.selectedGoalValueType[i] = 'Rate';
        this.selectedGoalType[i] = this.GoalGroupConfigs.Viewability.label;
      } else if (this.goalId(i).value === this.goalTypeEnum.ENGAGEMENT_CPCV) {
        this.selectedGoalValueType[i] = this.CPCV.label;
        this.selectedGoalType[i] = this.GoalGroupConfigs.Completion.label;
      } else if (this.goalId(i).value === this.goalTypeEnum.VIEWABILITY_VCPM) {
        this.selectedGoalValueType[i] = this.VCPM.label;
        this.selectedGoalType[i] = this.GoalGroupConfigs.Viewability.label;
      }
    }
    // HardGoal value should be true for the goal which has rank 0
    this.goalControls[0].get('hardGoal').patchValue(true);
  }

  //Add goal schedule row.
  addGoal(isDefault: boolean = false) {
    if (!isDefault && (this.goals.length >= 2 || this.goals.disabled)) return;
    let newGoal = new GoalDTO(this.fb);
    if (this.goals.value.length === 1) {
      this.goals.push(newGoal.formGroup);
      this.selectedGoalValueType[1] = 'Rate';
      if (this.goals.value[0].goalTypeId === this.goalTypeEnum.DELIVERY_ENGAGEMENT) {
        this.selectedGoalType[1] = this.GoalGroupConfigs.Viewability.label;
        this.goalId(1).patchValue(this.goalTypeEnum.DELIVERY_VIEWABILITY);
      } else {
        this.selectedGoalType[1] = this.GoalGroupConfigs.Completion.label;
        this.goalId(1).patchValue(this.goalTypeEnum.DELIVERY_ENGAGEMENT);
      }
      this.goalRank(1).patchValue(1);
    } else {
      this.goals.push(newGoal.formGroup);
      this.updateSelectedGoals();
    }

  }

  //Reset entire goals, will keep only first goal
  resetGoal() {
    if (this.goals.disabled) return;
    else if (this.goals.length === 2) {
      this.goals.controls.length = 1;
      this.goals.removeAt(1);
    }
  }

  //remove single row of goal by clicking on "X"
  removeGoal(index: number) {
    if (this.goals.disabled) return;
    else if (index > 0) {
      this.goals.removeAt(index);
      if (this.goals.length === 1) {
        /**
         * If user delete goal from first place, we need to change goal rank
         * To make secondary goal as primary goal
         */
        this.goalRank(0).patchValue(0);
        this.updateSelectedGoals();
      }
    }
  }

  //Setting the currency & delivery threshold input based on goal value change.
  onGoalDropdownValueChange(event, index) {
    if (typeof event === 'string') {
      return;
    }
    this.selectedGoalValueType[index] = event.target.value;
    if (this.selectedGoalValueType[index] === 'Rate') {
      let thresholdValue = this.goalDeliveryThreshold(index).value || 50;
      this.goalDeliveryThreshold(index).patchValue(thresholdValue);
      this.goalDeliveryThreshold(index).setValidators([Validators.required, Validators.min(1), Validators.max(100)]);
      this.goalDeliveryThreshold(index).updateValueAndValidity();
      this.goalCurrencyValue(index).setValidators(null);
      this.goalCurrencyValue(index).patchValue(null);
    } else if (this.selectedGoalValueType[index] === this.CPCV.label || this.selectedGoalValueType[index] === this.VCPM.label) {
      // setting this validator in asynchronous manner bc of the digestion check for ngValid
      setTimeout(() => {
        this.goalCurrencyValue(index).setValidators([Validators.required, Validators.min(0), Validators.max(100)]);
        this.goalCurrencyValue(index).updateValueAndValidity();
      }, 0);
      this.goalDeliveryThreshold(index).setValidators(null);
      this.goalDeliveryThreshold(index).patchValue(null);
    }
    this.setOptimizedGoalId(index);
  }

  goalTypeChange(event: IOptionProps, index) {
    this.goalCurrencyValue(index).patchValue(null);
    this.selectedGoalType[index] = event.value;
    this.goalDeliveryThreshold(index).patchValue(50);
    this.setOptimizedGoalId(index);
  }

  viewabilityMeasurementChange(event: IOptionProps, index) {
    this.viewabilityMeasurementId(index).patchValue(this.viewabilityMeasurement[event.value].id);
  }

  getSelectedViewabilityMeasurement(index) {
    return _.find(this.viewabilityMeasurement, { id: this.viewabilityMeasurementId(index).value })
  }

  setOptimizedGoalId(index) {
    if (this.selectedGoalType[index] === this.GoalGroupConfigs.Viewability.label) {
      if (this.selectedGoalValueType[index] === 'Rate') {
        this.goalId(index).patchValue(this.goalTypeEnum.DELIVERY_VIEWABILITY);
      } else if (this.selectedGoalValueType[index] === this.VCPM.label) {
        this.goalId(index).patchValue(this.goalTypeEnum.VIEWABILITY_VCPM);
      } else if (this.selectedGoalValueType[index] === this.CPCV.label) {
        this.selectedGoalValueType[index] = 'Rate';
        this.goalId(index).patchValue(this.goalTypeEnum.DELIVERY_VIEWABILITY);
      }
    } else if (this.selectedGoalType[index] === this.GoalGroupConfigs.Completion.label) {
      if (this.selectedGoalValueType[index] === "Rate") {
        this.goalId(index).patchValue(this.goalTypeEnum.DELIVERY_ENGAGEMENT);
      } else if (this.selectedGoalValueType[index] === this.CPCV.label) {
        this.goalId(index).patchValue(this.goalTypeEnum.ENGAGEMENT_CPCV);
      } else if (this.selectedGoalValueType[index] === this.VCPM.label) {
        this.selectedGoalValueType[index] = 'Rate';
        this.goalId(index).patchValue(this.goalTypeEnum.DELIVERY_ENGAGEMENT);
      }
    }
    this.compatibleGoalStacking();
    this.goalId(0).setValidators([this.goalTypeIdCompatibleValidator(this.compatible)]);
    this.goalId(0).updateValueAndValidity();
  }

  //check if goal type can be stacked with primary goal
  compatibleGoalStacking() {
    this.compatible = true;
    if (this.goals.value.length === 2) {
      if (this.goalId(0).value === GoalType.DELIVERY_VIEWABILITY) {
        this.compatible = this.goalId(1).value === GoalType.DELIVERY_ENGAGEMENT;
      } else if (this.goalId(0).value === GoalType.DELIVERY_ENGAGEMENT) {
        this.compatible = this.goalId(1).value === GoalType.DELIVERY_VIEWABILITY;
      } else if (this.goalId(0).value === GoalType.VIEWABILITY_VCPM) {
        this.compatible = this.goalId(1).value === GoalType.DELIVERY_VIEWABILITY || this.goalId(1).value === GoalType.DELIVERY_ENGAGEMENT;
      } else if (this.goalId(0).value === GoalType.ENGAGEMENT_CPCV) {
        this.compatible = this.goalId(1).value === GoalType.DELIVERY_VIEWABILITY || this.goalId(1).value === GoalType.DELIVERY_ENGAGEMENT;
      }
    }
  }

  isInvalid() {
    for (let goal of this.goals.controls) {
      var goalGroup = goal as FormGroup;
      if (goalGroup.controls.goalCurrencyValue.errors || goalGroup.controls.deliveryGoalThreshold.errors) {
        return true;
      }
    }
    return false;
  }

  goalTypeIdCompatibleValidator(compatible: boolean): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      return !compatible ? { goalTypeIdCompatible: { value: control.value } } : null;
    };
  }

  updateHardGoal(value: any, index: number) {
    if (_.isBoolean(value)) {
      this.hardGoalControl(index).patchValue(value);
    }
  }

  viewabilityMeasurementId(index): FormControl {
    return (this.goalControls[index] as FormGroup).controls.viewabilityMeasurementId as FormControl
  }

  checkEnvironmentChange(): void {
    this.isMobileAppEnvSelected = _.includes(this.environments.value, EnvironmentConst.MobileApps.id)
    this.environments.valueChanges.subscribe(envs => {
      this.isMobileAppEnvSelected = _.includes(envs, EnvironmentConst.MobileApps.id);
    });
  }

  get goals(): FormArray {
    return <FormArray>this.formArray.controls[this.group].get('goals');
  }

  get goalControls() {
    return this.goals.controls;
  }

  goalId(index): FormControl {
    return (this.goalControls[index] as FormGroup).controls.goalTypeId as FormControl;
  }

  goalDeliveryThreshold(index): FormControl {
    return (this.goalControls[index] as FormGroup).controls.deliveryGoalThreshold as FormControl;
  }

  goalCurrencyValue(index): FormControl {
    return (this.goalControls[index] as FormGroup).controls.goalCurrencyValue as FormControl;
  }

  goalRank(index): FormControl {
    if (this.goalControls.length) {
      return (this.goalControls[index] as FormGroup).controls.rank as FormControl;
    }
  }

  get isDisabled(): boolean {
    return this.goals.disabled;
  }

  get videoViewabilityCPM(): number {
    return this.formArray.controls[this.group].get('videoViewabilityCPM').value;
  }

  get videoViewabilityPartner(): number {
    return this.formArray.controls[this.group].get('videoViewabilityPartner').value;
  }

  get mobileViewabilityPartner(): number {
    return this.formArray.controls[this.group].get('mobileViewabilityPartner').value;
  }

  get mobileViewabilityCPM(): number {
    return this.formArray.controls[this.group].get('mobileViewabilityCPM').value;
  }

  get environments() {
    return this.formArray.controls[this.group].get("environments") as FormControl;
  }

  hardGoalControl(index): FormControl {
    return (this.goalControls[index] as FormGroup).controls.hardGoal as FormControl;;
  }

  onDrop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.goals.controls, event.previousIndex, event.currentIndex);
    this.updateSelectedGoals();
    /**
     * After drag & drop, we need to change rank of added goals
     * i.e primary to secondary and vice versa
     * */
    this.goalRank(0).patchValue(0);
    this.goalRank(1).patchValue(1);
  }
}